import { useContext } from "react";
import type { CSSProperties } from "styled-components/macro";
import styled, { ThemeContext } from "styled-components/macro";
import type { ProductStatusType } from "../../types/types.PIM";
import type { TFunction } from "react-i18next";
import { CheckBoxFinePrintLabel } from "../Form/Form";
import { TextSmMedium } from "../Typography/TypographyV2";
import { InfoIcon } from "../Icons/Icons";
import { borderPrimitiveTokens } from "../../designTokens";
import { ChangeRequestStatusType } from "../ChangeRequestForm/changerequest.util";

const StatusState = styled.div`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-transform: capitalize;
  white-space: nowrap;
`;

const StatusContainer = styled.div`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  text-transform: capitalize;
  padding: 0 ${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: 3px;
`;

const LeftDot = styled.span`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: ${({ theme }) => theme.resolve("spacing_md")};
`;

const LeftDotV2 = styled.span`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const RightDot = styled.span`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-left: ${({ theme }) => theme.resolve("spacing_md")};
`;

const StatusBorder = styled.div<{ statusColor?: string }>`
  > div {
    display: inline-flex;
    align-items: center;
    border-radius: ${({ theme }) => theme.resolve("radius_xs")};
    border: ${({ theme }) => theme.resolve("border_xs")} solid
      ${({ statusColor }) => statusColor};
    background: ${({ theme }) => theme.resolve("color_bg_surface_caution")};
    padding: ${({ theme }) => theme.resolve("spacing_xxs")}
      ${({ theme }) => theme.resolve("spacing_xs")};
  }
`;

export type StatusColor =
  | "blue"
  | "fullBlue"
  | "green"
  | "orange"
  | "red"
  | "gray"
  | "yellow";

export type ProductStatusColor = "green" | "orange" | "yellow" | "gray" | "red";
export type ChangeRequestStatusColor = "green" | "blue" | "red";

export type StatusPropsBase = {
  text: string;
  textStyle?: CSSProperties;
  id?: string;
};

export type ProductStatus = {
  color: ProductStatusColor;
  productStatusType?: "product";
};

export type DefaultStatus = {
  color: StatusColor;
  productStatusType?: "default";
};

export type IStatusProps = StatusPropsBase & (ProductStatus | DefaultStatus);

export type BarentzApiStatusType =
  | "success"
  | "failed"
  | "not_pushed"
  | "in_progress"
  | "out_of_sync";

export const BARENTZ_API_STATUS_CONFIG: Record<
  BarentzApiStatusType,
  { color: StatusColor; labelKey: string }
> = {
  success: { color: "green", labelKey: "Synced" },
  failed: { color: "red", labelKey: "Failed" },
  not_pushed: { color: "gray", labelKey: "Not Pushed" },
  in_progress: { color: "yellow", labelKey: "In Progress" },
  out_of_sync: { color: "orange", labelKey: "Out of Sync" },
};

export const DEFAULT_BARENTZ_API_STATUS = {
  color: "gray" as StatusColor,
  labelKey: "Not Pushed",
};

export const getBarentzApiStatusConfig = (
  status: string | null | undefined,
  t: TFunction
): { color: StatusColor; text: string } => {
  const config =
    (status && status in BARENTZ_API_STATUS_CONFIG
      ? BARENTZ_API_STATUS_CONFIG[status as BarentzApiStatusType]
      : null) ?? DEFAULT_BARENTZ_API_STATUS;
  return { color: config.color, text: t(config.labelKey) };
};

const colorKeys: Record<
  StatusColor,
  { dotKey: string; textKey: string | null }
> = {
  blue: { dotKey: "primaryBlue", textKey: null },
  fullBlue: { dotKey: "primaryBlue", textKey: "primaryBlue" },
  green: { dotKey: "statusTextGreen", textKey: "mediumGreen" },
  orange: { dotKey: "moreBurntOrange", textKey: "burntOrange" },
  red: { dotKey: "red-2", textKey: "red-1" },
  gray: { dotKey: "gray-2", textKey: "gray-1" },
  yellow: { dotKey: "productStatusYellow", textKey: "yellow" },
};

const productColorKeys: Record<
  ProductStatusColor,
  { dotKey: string; borderKey: string; textKey?: string }
> = {
  green: { dotKey: "activeGreen", borderKey: "activeGreen" },
  orange: { dotKey: "productStatusOrange", borderKey: "productStatusOrange" },
  yellow: { dotKey: "productStatusYellow", borderKey: "productStatusYellow" },
  gray: { dotKey: "grayText", borderKey: "grayText" },
  red: { dotKey: "errorRedBorder", borderKey: "errorRedBorder" },
};

const getTestId = (id: string | undefined) =>
  id ? `status-text-${id}` : "status-text";

export const StatusLeft = ({
  color,
  text,
  textStyle,
  id,
  productStatusType = "default",
}: IStatusProps) => {
  const theme = useContext(ThemeContext);
  const { dotKey, textKey } =
    productStatusType === "default"
      ? colorKeys[color as StatusColor]
      : productColorKeys[color as ProductStatusColor];
  return (
    <StatusState style={textKey ? { color: theme.colors[textKey] } : undefined}>
      <LeftDot style={{ backgroundColor: theme.colors[dotKey] }} />
      <CheckBoxFinePrintLabel htmlFor={id} style={textStyle}>
        {text}
      </CheckBoxFinePrintLabel>
    </StatusState>
  );
};

export const StatusRight = ({
  color,
  text,
  textStyle,
  id,
  productStatusType = "default",
}: IStatusProps) => {
  const theme = useContext(ThemeContext);
  const { dotKey, textKey } =
    productStatusType === "default"
      ? colorKeys[color as StatusColor]
      : productColorKeys[color as ProductStatusColor];
  return (
    <StatusState style={textKey ? { color: theme.colors[textKey] } : undefined}>
      <span style={textStyle} data-testid={getTestId(id)}>
        {text}
      </span>
      <RightDot style={{ backgroundColor: theme.colors[dotKey] }} />
    </StatusState>
  );
};

export const StatusBar = ({
  color,
  text,
  textStyle,
  id,
  productStatusType = "default",
}: IStatusProps) => {
  const theme = useContext(ThemeContext);
  const { textKey } =
    productStatusType === "default"
      ? colorKeys[color as StatusColor]
      : productColorKeys[color as ProductStatusColor];
  return (
    <StatusContainer
      style={
        textKey
          ? {
              color: theme.colors[textKey],
              border: `${borderPrimitiveTokens.border_md} solid ${theme.colors[textKey]}`,
            }
          : undefined
      }
    >
      <span style={textStyle} data-testid={getTestId(id)}>
        {text}
      </span>
    </StatusContainer>
  );
};

const productStatusColorKeysV2: Record<
  StatusColor,
  { dotKey: string; borderKey: string; textKey?: string }
> = {
  blue: { dotKey: "primaryBlue", borderKey: "primaryBlue" },
  fullBlue: { dotKey: "primaryBlue", borderKey: "primaryBlue" },
  green: { dotKey: "activeGreen", borderKey: "activeGreen" },
  orange: { dotKey: "productStatusOrange", borderKey: "productStatusOrange" },
  yellow: { dotKey: "productStatusYellow", borderKey: "productStatusYellow" },
  gray: { dotKey: "gray-1", borderKey: "secondaryLightGray" },
  red: { dotKey: "errorRedBorder", borderKey: "errorRedBorder" },
};

// Keep the old StatusWithBorder for backward compatibility
export const StatusWithBorderLegacy = ({
  status,
  t,
}: {
  status: ProductStatusType;
  t: TFunction;
}) => {
  const theme = useContext(ThemeContext);
  const color = getProductStatusColor(status);
  const { borderKey } = productStatusColorKeysV2[color];
  const { dotKey, textKey } =
    productStatusColorKeysV2[color as ProductStatusColor];
  return (
    <StatusBorder statusColor={theme.colors[borderKey]}>
      <StatusState
        style={textKey ? { color: theme.colors[textKey] } : undefined}
      >
        <LeftDotV2 style={{ backgroundColor: theme.colors[dotKey] }} />
        <span>{getProductStatusText(status, t)}</span>
      </StatusState>
    </StatusBorder>
  );
};

export const getProductStatusText = (
  status: ProductStatusType,
  t: TFunction
): string => {
  switch (status) {
    case "draft":
    case "creation_requested":
      return t("Draft");
    case "staged":
      return t("Staged Changes");
    case "published":
      return t("Published");
    case "unpublished":
      return t("Unpublished");
    case "unpublished_staged":
      return t("Unpublished Staged");
    case "archived":
      return t("Archived");
    case "discarded":
      return t("Discarded");
    case "bulk_edit_in_progress":
      return t("Bulk Edit in Progress");
    default:
      console.error("!! UNKNOWN STATUS !!");
      return "";
  }
};

export const getProductStatusValue = (
  status: string,
  t: TFunction
): ProductStatusType => {
  switch (status) {
    case t("Draft"):
      return "draft";
    case t("Staged Changes"):
      return "staged";
    case t("Published"):
      return "published";
    case t("Unpublished"):
      return "unpublished";
    case t("Unpublished Staged"):
      return "unpublished_staged";
    case t("Archived"):
      return "archived";
    case t("Discarded"):
      return "discarded";
    case t("Bulk Edit in Progress"):
      return "bulk_edit_in_progress";
    case t("Creation Requested"):
      return "creation_requested";
    default:
      console.error("!! UNKNOWN STATUS !!", status);
      // Return the original status as fallback instead of always "draft"
      return "draft";
  }
};

export const getProductStatusColor = (
  status: ProductStatusType
): ProductStatusColor => {
  switch (status) {
    case "draft":
    case "creation_requested":
      return "orange";
    case "staged":
      return "yellow";
    case "published":
      return "green";
    case "unpublished":
      return "gray";
    case "unpublished_staged":
      return "gray";
    case "archived":
      return "red";
    case "discarded":
      return "red";
    case "bulk_edit_in_progress":
      return "red";
    default:
      console.error("!! UNKNOWN STATUS !!");
      return "red";
  }
};

export const getChangeRequestStatusColor = (
  status: ChangeRequestStatusType
): ChangeRequestStatusColor => {
  switch (status) {
    case "new":
      return "blue";
    case "accepted":
      return "green";
    case "discarded":
      return "red";
    default:
      console.error("!! UNKNOWN STATUS !!");
      return "red";
  }
};

const StatusBadgeDot = styled.span`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
`;

const StatusBadgeText = styled(TextSmMedium)`
  white-space: nowrap;
`;

const StatusBadgeContainer = styled.div<{
  statusColor?: string;
  bgColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  border: ${({ theme }) => theme.resolve("border_xs")} solid
    ${({ statusColor }) => statusColor};
  background: ${({ bgColor, theme }) =>
    bgColor || theme.resolve("color_bg_surface_caution")};
  padding: ${({ theme }) => theme.resolve("spacing_xxs")}
    ${({ theme }) => theme.resolve("spacing_xs")};
`;

const statusBadgeColorKeys: Record<
  StatusColor,
  { dotKey: string; borderKey: string; textKey?: string }
> = {
  blue: { dotKey: "primaryBlue", borderKey: "primaryBlue" },
  fullBlue: { dotKey: "primaryBlue", borderKey: "primaryBlue" },
  green: { dotKey: "activeGreen", borderKey: "activeGreen" },
  orange: { dotKey: "productStatusOrange", borderKey: "productStatusOrange" },
  yellow: { dotKey: "productStatusYellow", borderKey: "productStatusYellow" },
  gray: { dotKey: "gray-1", borderKey: "secondaryLightGray" },
  red: { dotKey: "errorRedBorder", borderKey: "errorRedBorder" },
};

export type StatusBadgeProps = {
  /** The text to display in the status badge */
  text: string;
  /** The color scheme to use for the badge */
  color: StatusColor;
  /** Optional custom background color. If not provided, uses caution background */
  backgroundColor?: string;
  /** Optional test ID */
  id?: string;
};

/**
 * A generic status badge component with a bordered container.
 *
 * This component displays status information with:
 * - A colored dot on the left
 * - Custom status text
 * - A bordered container with colored border matching the status color
 * - Configurable background color (defaults to caution)
 *
 * @example
 * ```tsx
 * <StatusBadge
 *   text="In Progress"
 *   color="orange"
 * />
 *
 * <StatusBadge
 *   text="Approved"
 *   color="green"
 *   backgroundColor="#f4fbf7"
 * />
 * ```
 */
export const StatusBadge = ({
  text,
  color,
  backgroundColor,
  id,
}: StatusBadgeProps) => {
  const theme = useContext(ThemeContext);
  const { dotKey, borderKey } = statusBadgeColorKeys[color];

  return (
    <StatusBadgeContainer
      statusColor={theme.colors[borderKey]}
      bgColor={backgroundColor}
    >
      <StatusBadgeDot style={{ backgroundColor: theme.colors[dotKey] }} />
      <StatusBadgeText data-testid={id ? `status-badge-${id}` : undefined}>
        {text}
      </StatusBadgeText>
    </StatusBadgeContainer>
  );
};

/**
 * A status component specifically designed for product statuses with a bordered container.
 *
 * This component automatically determines the appropriate color based on the
 * product status type and displays it with:
 * - A colored dot on the left
 * - Localized status text
 * - A bordered container with colored border matching the status color
 * - A caution-colored background
 * @param props - The component props
 * @param props.status - The product status type enum value
 * @param props.t - The translation function from react-i18next
 *
 * @example
 * ```tsx
 * <StatusWithBorder
 *   status="published"
 *   t={t}
 * />
 * ```
 */
export const StatusWithBorder = ({
  status,
  t,
}: {
  status: ProductStatusType;
  t: TFunction;
}) => {
  const color = getProductStatusColor(status);
  const text = getProductStatusText(status, t);
  return <StatusBadge text={text} color={color} />;
};

export type APIStatusProps = {
  text: string;
  color: StatusColor;
  id?: string;
  timestamp: string;
  onInfoClick?: () => void;
};

/**
 * API Status component showing a status badge with a timestamp below.
 *
 * Matches the Agilis Design System "Type=API Status" variant: a vertical stack
 * with a StatusBadge on top and a tertiary-styled timestamp underneath. Supports
 * a hover state (subtle blue tint) on the wrapper.
 *
 * @param props - Component props
 * @param props.text - Status label displayed in the badge
 * @param props.color - Status color (e.g. green, orange, red)
 * @param props.backgroundColor - Optional custom badge background
 * @param props.id - Optional id for test hooks and a11y
 * @param props.timestamp - Timestamp string displayed below the badge
 * @param props.onInfoClick - Optional click handler for an inline info icon
 *
 * @example
 * ```tsx
 * <APIStatus
 *   text="Published"
 *   color="green"
 *   timestamp="Feb 27 2026 at 10:30 AM"
 * />
 *
 * <APIStatus
 *   text="Synced"
 *   color="green"
 *   timestamp="MMM DD YYYY at HH:MM AM"
 *   id="product-status"
 * />
 * ```
 */
const APIStatusInfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: ${({ theme }) => theme.resolve("radius_buttonIcon_standard")};
  border: ${({ theme }) => theme.resolve("border_none")} solid
    ${({ theme }) => theme.resolve("color_buttonIcon_border_enabled")};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  opacity: 0;
  pointer-events: none;
  background-color: transparent;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const APIStatusWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xxs")}
    ${({ theme }) => theme.resolve("spacing_xs")};
  transition: background 0.15s ease;

  &:hover ${APIStatusInfoButton} {
    opacity: 1;
    pointer-events: auto;
  }
`;

const APIStatusHeaderRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const TimestampComponent = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

export const APIStatus = ({
  text,
  color,
  id,
  timestamp,
  onInfoClick,
}: APIStatusProps) => {
  return (
    <APIStatusWrapper data-testid={id ? `api-status-${id}` : undefined}>
      <APIStatusHeaderRow>
        <StatusBadge color={color} text={text} id={id} />
        {onInfoClick && (
          <APIStatusInfoButton
            type="button"
            aria-label="API status"
            onClick={onInfoClick}
          >
            <InfoIcon />
          </APIStatusInfoButton>
        )}
      </APIStatusHeaderRow>
      <TimestampComponent>{timestamp}</TimestampComponent>
    </APIStatusWrapper>
  );
};
